<template>
  <th v-if="isHeader">
    {{'Actions' | translate}}
  </th>
  <td v-else>
    <div class="stock-actions">
      <b-button-group>
        <b-button size="sm" variant="primary" class="mr-1" @click="openHistory(rowData)">
          {{ $t('History') }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="openTransaction(rowData.id)">
          {{ $t('Manual Adjustment') }}
        </b-button>
      </b-button-group>

      <!-- History Modal -->
      <b-modal v-model="showHistoryModal" :title="$t('Stock History')" size="lg">
        <div class="d-flex justify-content-end mb-2">
          <b-button size="sm" variant="outline-secondary" @click="showChart = !showChart">
            <i class="fa fa-line-chart"></i>
            {{ showChart ? $t('Hide chart') : $t('Show chart') }}
          </b-button>
        </div>
        <div v-if="showChart" class="stock-history-chart-wrap mb-3">
          <stock-history-chart v-if="chartData" :chart-data="chartData" :height="240"></stock-history-chart>
          <div v-else class="text-muted text-center p-3">{{ $t('No data to chart') }}</div>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th>{{ $t('Date') }}</th>
              <th>{{ $t('Type') }}</th>
              <th>{{ $t('Quantity') }}</th>
              <th>{{ $t('Reason') }}</th>
              <th>{{ $t('User') }}</th>
              <th class="text-right">{{ $t('Stock After') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transaction in historyWithRunning" :key="transaction.id">
              <td>{{ transaction.created_at }}</td>
              <td>{{ typeTranslations[transaction.type] }}</td>
              <td>
                {{ transaction.quantity }}
                <small v-if="transaction.audit_delta !== undefined" class="text-muted">(Δ {{ formatDelta(transaction.audit_delta) }})</small>
              </td>
              <td>{{ transaction.reason }}</td>
              <td>{{ transaction.user.firstname }} {{ transaction.user.surname }}</td>
              <td class="text-right">{{ transaction.running }}</td>
            </tr>
          </tbody>
        </table>
      </b-modal>
    </div>
  </td>
</template>

<script>
import VuetableFieldMixin from 'vuetable-2/src/components/VuetableFieldMixin'
import StockHistoryChart from './StockHistoryChart'

export default {
  name: 'vue-table-stock-actions',
  mixins: [VuetableFieldMixin],
  components: { StockHistoryChart },
  props: ['rowData', 'rowIndex'],
  data () {
    return {
      showHistoryModal: false,
      showChart: false,
      history: []
    }
  },
  computed: {
    typeTranslations() {
      return {
        'IN': this.$t('Stock In'),
        'OUT': this.$t('Stock Out'),
        'ADJUSTMENT': this.$t('Adjustment'),
        'AUDIT': this.$t('Audit')
      }
    },
    // Chronologically-ordered history with running totals. AUDIT rows don't
    // change the balance — the backend writes a paired ADJUSTMENT (inside one
    // DB transaction) whose signed delta is what actually moves stock. We
    // absorb that paired ADJUSTMENT into the AUDIT row for display and mirror
    // the backend's SUM(IN, OUT, ADJUSTMENT) when computing `running`.
    processedHistory() {
      var asc = this.history.slice().sort(function (a, b) {
        return new Date(a.created_at) - new Date(b.created_at)
      })
      var pairForAudit = {}
      var consumedAdjustmentIds = {}
      asc.forEach(function (a) {
        if (a.type !== 'AUDIT' || pairForAudit[a.id]) return
        for (var i = 0; i < asc.length; i++) {
          var b = asc[i]
          if (b.type !== 'ADJUSTMENT') continue
          if (consumedAdjustmentIds[b.id]) continue
          if (b.created_at !== a.created_at) continue
          if (b.created_user_id !== a.created_user_id) continue
          pairForAudit[a.id] = b
          consumedAdjustmentIds[b.id] = true
          break
        }
      })
      var running = 0
      var rows = []
      asc.forEach(function (t) {
        if (t.type !== 'AUDIT') {
          running += Number(t.quantity) || 0
        }
        if (consumedAdjustmentIds[t.id]) return
        var row = Object.assign({}, t)
        if (t.type === 'AUDIT' && pairForAudit[t.id]) {
          var delta = Number(pairForAudit[t.id].quantity) || 0
          row.audit_delta = delta
          row.running = running + delta
        } else {
          row.running = running
        }
        rows.push(row)
      })
      return rows
    },
    historyWithRunning() {
      return this.processedHistory.slice().reverse()
    },
    chartData() {
      var rows = this.processedHistory
      if (rows.length === 0) return null
      return {
        labels: rows.map(function (t) { return String(t.created_at).slice(0, 16) }),
        datasets: [{
          label: 'Stock level',
          data: rows.map(function (t) { return t.running }),
          borderColor: '#20a8d8',
          backgroundColor: 'rgba(32, 168, 216, 0.1)',
          fill: true
        }]
      }
    }
  },
  methods: {
    async openHistory (row) {
      try {
        const response = await this.$http.get(`stock/${row.id}/history`)
        this.history = response.data
        this.showChart = false
        this.showHistoryModal = true
      } catch (error) {
        this.$notify({
          group: 'full',
          type: 'error',
          text: this.$t('Failed to load history')
        })
      }
    },
    openTransaction (articleId) {
      this.$parent.$parent.$parent.openTransactionModal(articleId)
    },
    formatDelta (n) {
      return n > 0 ? '+' + n : String(n)
    }
  }
}
</script>

<style scoped>
.stock-history-chart-wrap {
  position: relative;
  height: 240px;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// VueTableStockActions.vue?78c0af10